import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useOrganization } from '../../shared/hooks/use-organization';
import { useOrganizationApplications } from '../../shared/OrganizationApplicationsContext';
import { findHighestPriorityApplication } from '../../credentials/utils/utils';
import { useLogoUpload } from './useLogoUpload';
import { analyticsClient } from '../../utils/analytics/client';
import * as Sentry from '@sentry/react';

export function useBrandSettingsState() {
  const brandingOrg = useOrganization();
  const applications = useOrganizationApplications();
  const productionApp = findHighestPriorityApplication(applications);
  const [localEnabled, setLocalEnabled] = useState<boolean>(
    brandingOrg.branding.data?.aliasEnabled ?? true,
  );
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [editedName, setEditedName] = useState<string>('');
  const [initialDisplayName, setInitialDisplayName] = useState<
    string | undefined
  >(brandingOrg.branding.data?.displayName);
  const [initialAliasEnabled, setInitialAliasEnabled] = useState<boolean>(
    brandingOrg.branding.data?.aliasEnabled ?? true,
  );
  const [initialLogoPreview, setInitialLogoPreview] = useState<string | null>(
    brandingOrg.branding.data?.logo || null,
  );
  const [logoRemoved, setLogoRemoved] = useState<boolean>(false);
  const [brandingOverride, setBrandingOverride] = useState<any>(null);

  // Logo upload state/handlers
  const {
    isCropImageOpen,
    setIsCropImageOpen,
    logoFile,
    setLogoFile,
    logoPreview,
    setLogoPreview,
    prevLogoFile,
    prevLogoPreview,
    onDrop,
    handleCropSave,
    getScaledLogoFile,
  } = useLogoUpload(brandingOrg.branding.data ?? {});
  const {
    getRootProps: getLogoDropzoneRootProps,
    getInputProps: getLogoDropzoneInputProps,
  } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
  });
  const {
    getRootProps: getAddLogoButtonRootProps,
    getInputProps: getAddLogoButtonInputProps,
  } = useDropzone({
    onDrop,
    noDrag: true,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxFiles: 1,
  });

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setLogoRemoved(true);
    setBrandingOverride({
      ...brandingOrg.branding,
      data: {
        ...brandingOrg.branding.data,
        logo: null,
      },
    });
  };

  // Wrapper for setLocalEnabled that includes analytics tracking
  const handleSetLocalEnabled = (enabled: boolean) => {
    setLocalEnabled(enabled);
    analyticsClient.track('branding_alias_toggled', { enabled });
  };

  // Sync initial values when branding.data changes
  useEffect(() => {
    setInitialDisplayName(brandingOrg.branding.data?.displayName);
    setInitialAliasEnabled(brandingOrg.branding.data?.aliasEnabled ?? true);
    setInitialLogoPreview(brandingOrg.branding.data?.logo || null);
    setLocalEnabled(brandingOrg.branding.data?.aliasEnabled ?? true);
    setLogoFile(null);
    setLogoPreview(brandingOrg.branding.data?.logo || null);
    setEditedName('');
    setIsEditingName(false);
    setLogoRemoved(false); // Reset flag when branding data changes
    setBrandingOverride(null); // Reset branding override when org data changes
  }, [brandingOrg.branding.data, setLogoFile, setLogoPreview]);

  // Detect if any field has changed
  const hasChanges = () => {
    const nameChanged =
      isEditingName && editedName.trim() !== (initialDisplayName || '');
    const toggleChanged = localEnabled !== initialAliasEnabled;
    const logoChanged = logoPreview !== initialLogoPreview;
    return nameChanged || toggleChanged || logoChanged;
  };
  const handleSave = async () => {
    if (isEditingName && editedName.length > 50) return;
    const formData = new FormData();

    // Track what's being uploaded
    const hasLogo = !!logoFile;
    const hasDisplayName = isEditingName && editedName.trim() !== '';
    const hasAliasEnabled = localEnabled !== initialAliasEnabled;

    // Only send displayName if it was edited (even if empty)
    if (isEditingName) {
      // If the edited name is different from the initial, or if it's empty
      if (
        editedName.trim() !== (initialDisplayName || '') ||
        editedName.trim() === ''
      ) {
        formData.append('displayName', editedName.trim());
      }
    }

    // Only send aliasEnabled if it changed
    if (localEnabled !== initialAliasEnabled) {
      formData.append('aliasEnabled', String(localEnabled));
    }

    // Only send logo if it changed (added or removed)
    if (logoFile) {
      // Scale the logo to 256x256 before sending to backend
      const scaledLogoFile = await getScaledLogoFile();
      if (scaledLogoFile) {
        formData.append('logo', scaledLogoFile);
      }
    } else if (logoRemoved) {
      // Explicitly send logo as empty string if it was removed
      formData.append('logo', '');
    }

    // Only submit if something changed
    if (Array.from(formData.keys()).length > 0) {
      // Track successful upload
      brandingOrg.updateBranding.mutate(formData, {
        onSuccess: () => {
          analyticsClient.track('branding_upload_success', {
            hasLogo,
            hasDisplayName,
            hasAliasEnabled,
          });
          Sentry.captureMessage('Branding upload successful', {
            level: 'info',
            tags: {
              hasLogo: String(hasLogo),
              hasDisplayName: String(hasDisplayName),
              hasAliasEnabled: String(hasAliasEnabled),
            },
          });
        },
        onError: (error) => {
          analyticsClient.track('branding_upload_failed', {
            error: error?.message || 'Unknown error',
            hasLogo,
            hasDisplayName,
            hasAliasEnabled,
          });
          Sentry.captureException(error, {
            tags: {
              hasLogo: String(hasLogo),
              hasDisplayName: String(hasDisplayName),
              hasAliasEnabled: String(hasAliasEnabled),
            },
          });
        },
      });
    }
  };
  const handleCancel = () => {
    setLocalEnabled(initialAliasEnabled);
    setLogoPreview(initialLogoPreview);
    setLogoFile(null);
    setEditedName('');
    setIsEditingName(false);
    setLogoRemoved(false);
    setBrandingOverride(null);
  };
  const getBrandDisplayName = (): string => {
    const displayNameVal = brandingOrg.branding.data?.displayName;
    if (brandingOrg.branding.data?.aliasEnabled) {
      if (displayNameVal && displayNameVal.trim() !== '') return displayNameVal;
      return productionApp?.name || '<your application name>';
    }
    return 'This Application';
  };
  return {
    localEnabled,
    setLocalEnabled: handleSetLocalEnabled,
    isEditingName,
    setIsEditingName,
    editedName,
    setEditedName,
    logoFile,
    setLogoFile,
    logoPreview,
    setLogoPreview,
    prevLogoFile,
    prevLogoPreview,
    isCropImageOpen,
    setIsCropImageOpen,
    onDrop,
    handleRemoveLogo,
    handleCropSave,
    getBrandDisplayName,
    branding: brandingOverride || brandingOrg.branding,
    updateBranding: brandingOrg.updateBranding,
    getLogoDropzoneRootProps,
    getLogoDropzoneInputProps,
    getAddLogoButtonRootProps,
    getAddLogoButtonInputProps,
    hasChanges,
    handleSave,
    handleCancel,
    logoRemoved,
  };
}
